import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { emailAddressRule } from '@client/utils/validation/email-address';
import { isValidEmailAddress } from '@shared/utils/core/email-address';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input, Switch } from 'antd';
import { useId } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/handling-parties/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'New handling party',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <HandlingPartyAdd />
        </PageController>
    );
}

type HandlingParty = RouterInputs['handlingParty']['createHandlingParty'];

const FormItem = Form.Item<HandlingParty>;

function HandlingPartyAdd() {
    const formId = useId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.handlingParty.createHandlingParty.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<HandlingParty>();

    const formDirty = useFormDirty();

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title="New handling party"
            afterClose={() => {
                form.resetFields();
                void trpcUtils.handlingParty.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ requestClose }) => <DrawerFooter onCancel={requestClose} formId={formId} />}
        >
            {({ onClose }) => (
                <Form
                    id={formId}
                    layout="vertical"
                    form={form}
                    {...formDirty.formProps}
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(values, {
                            onSuccess() {
                                message.success('Saved.');
                                onClose();
                            },
                            onError(error) {
                                if (error.data?.errorId === 'handlingParty.alreadyExists') {
                                    message.destroy();

                                    form.setFields([
                                        {
                                            name: 'name',
                                            errors: ['This name is already in use'],
                                        },
                                    ]);

                                    return;
                                }

                                errorMessage.show(error);
                            },
                        });
                    }}
                >
                    <FormItem label="Name" name="name" rules={[{ required: true }]}>
                        <Input />
                    </FormItem>
                    <FormItem
                        label="Notification email"
                        name="notificationEmail"
                        rules={[emailAddressRule(isValidEmailAddress)]}
                        extra="Receives internal notifications for lodged claims. Leave blank to skip this handling party."
                    >
                        <Input type="email" maxLength={320} />
                    </FormItem>
                    <FormItem label="Is disabled?" name="isDisabled">
                        <Switch />
                    </FormItem>
                </Form>
            )}
        </OverlayPage>
    );
}
